/**
 * Voice session + turn state machine — SDD §20.4, normative:
 *
 *   idle → listening (push-to-talk ONLY — ESC-003: no open microphone, no wake word, no click-to-toggle)
 *        → transcribing (button released; the utterance is being finalised) → thinking → speaking → idle
 *   barge-in: speaking → listening, **context retained** (first-class transition, ADR/SDD §20.4)
 *   8s silence in `listening` → idle
 *   provider failure degrades realtime → pipeline → text; minutes exhausted degrades to text with an honest message
 *
 * Pure and deterministic: no timers, no I/O, no clock. The caller (useVoice) supplies events and timestamps, so the
 * machine is fully testable and replayable. The orchestrator is authoritative in production; this mirrors it client-side.
 */
export type VoiceState = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

/** The recoverable failure modes the directive names (§ESC-003): each has a clear UI state and a way back. */
export type VoiceFaultKind =
  'permission_denied' | 'audio_unavailable' | 'provider_timeout' | 'transcription_failed';
export type VoiceLane = 'realtime' | 'pipeline' | 'text';
export type TurnRole = 'user' | 'assistant';

export interface TranscriptLine {
  role: TurnRole;
  text: string;
  at: string;
  /** A partial line is still being transcribed (the HUD renders it dimmed). */
  partial?: boolean;
}
export interface VoiceContext {
  state: VoiceState;
  lane: VoiceLane;
  /** Retained across barge-in — the whole point of the transition. */
  transcript: TranscriptLine[];
  /** The utterance being captured in `listening`. */
  partial: string;
  /** Set while `thinking`; cleared when the answer arrives. */
  pendingQuery: string | null;
  /** Confirm-first: a workflow/journal write waits for an explicit confirmation turn (never an order — ADR-006). */
  confirmation: { intent: string; object: string } | null;
  /** Honest degradation copy shown in the HUD, or null. */
  degradeBanner: string | null;
  minutes: { used: number; quota: number; exhausted: boolean };
  /** Barge-in is only offered while speaking. */
  bargeInAvailable: boolean;
  /** A recoverable failure, rendered as its own HUD state until cleared. Distinct from `degradeBanner` (a lane
   *  change): a fault is a thing that stopped THIS turn, and "Try again" is its exit. */
  fault: { kind: VoiceFaultKind; message: string } | null;
  /** Milliseconds of silence accumulated in `listening` (8000 ends the turn). */
  silenceMs: number;
  /** Turn counter — every turn is audited (Phase 4 wires the audit row). */
  turns: number;
  lastLatencyMs: number | null;
}
export type VoiceEvent =
  | { type: 'ptt.down'; at: string }
  | { type: 'ptt.up'; at: string }
  | { type: 'partial'; text: string }
  | { type: 'endpoint'; at: string; text: string }
  | {
      type: 'answer';
      at: string;
      text: string;
      latencyMs: number;
      confirmation?: { intent: string; object: string } | null;
    }
  | { type: 'speech.end' }
  | { type: 'barge_in'; at: string }
  | { type: 'silence'; ms: number }
  | { type: 'degrade'; lane: VoiceLane; reason: string }
  | { type: 'minutes'; used: number; quota: number }
  | { type: 'fault'; kind: VoiceFaultKind; message: string }
  | { type: 'fault.clear' }
  | { type: 'end' };

export const SILENCE_TIMEOUT_MS = 8000;

export function initialVoice(overrides: Partial<VoiceContext> = {}): VoiceContext {
  return {
    state: 'idle',
    lane: 'realtime',
    transcript: [],
    partial: '',
    pendingQuery: null,
    confirmation: null,
    degradeBanner: null,
    minutes: { used: 0, quota: 120, exhausted: false },
    bargeInAvailable: false,
    fault: null,
    silenceMs: 0,
    turns: 0,
    lastLatencyMs: null,
    ...overrides,
  };
}

/** The transition table. Unknown events in a state are no-ops (the machine never lands in an undefined state). */
export function voiceReduce(c: VoiceContext, e: VoiceEvent): VoiceContext {
  switch (e.type) {
    case 'ptt.down':
      if (c.state === 'listening') return c;
      // barge-in is expressed as ptt.down while speaking; keep the transcript. Pressing clears a standing fault —
      // trying again IS the recovery.
      return { ...c, state: 'listening', partial: '', silenceMs: 0, bargeInAvailable: false, fault: null };
    case 'partial':
      return c.state === 'listening' ? { ...c, partial: e.text, silenceMs: 0 } : c;
    case 'ptt.up':
      // Releasing the control ends capture and FINALISES transcription (ESC-003) — a distinct state, because the
      // trader is waiting on the transcriber, not on the model, and the HUD should say which. Whether anything was
      // actually said is the ENDPOINT's decision: the transcriber may hold words no partial ever streamed.
      if (c.state !== 'listening') return c;
      return { ...c, state: 'transcribing', silenceMs: 0 };
    case 'endpoint': {
      if (c.state !== 'listening' && c.state !== 'transcribing') return c;
      const text = e.text.trim();
      if (!text) return { ...c, state: 'idle', partial: '', silenceMs: 0 };
      return {
        ...c,
        state: 'thinking',
        partial: '',
        silenceMs: 0,
        pendingQuery: text,
        transcript: [...c.transcript, { role: 'user', text, at: e.at }],
      };
    }
    case 'answer':
      if (c.state !== 'thinking') return c;
      return {
        ...c,
        state: 'speaking',
        pendingQuery: null,
        bargeInAvailable: true,
        lastLatencyMs: e.latencyMs,
        turns: c.turns + 1,
        confirmation: e.confirmation ?? null,
        transcript: [...c.transcript, { role: 'assistant', text: e.text, at: e.at }],
      };
    case 'speech.end':
      return c.state === 'speaking' ? { ...c, state: 'idle', bargeInAvailable: false } : c;
    case 'barge_in':
      // speaking → listening, context retained
      return c.state === 'speaking'
        ? { ...c, state: 'listening', partial: '', silenceMs: 0, bargeInAvailable: false }
        : c;
    case 'silence': {
      if (c.state !== 'listening') return c;
      const silenceMs = c.silenceMs + e.ms;
      return silenceMs >= SILENCE_TIMEOUT_MS
        ? { ...c, state: 'idle', partial: '', silenceMs: 0 }
        : { ...c, silenceMs };
    }
    case 'degrade':
      return { ...c, lane: e.lane, degradeBanner: e.reason };
    case 'minutes': {
      const exhausted = e.used >= e.quota;
      return {
        ...c,
        minutes: { used: e.used, quota: e.quota, exhausted },
        lane: exhausted ? 'text' : c.lane,
        degradeBanner: exhausted
          ? 'Voice minutes for this period are used up — continuing in text.'
          : c.degradeBanner,
      };
    }
    case 'fault':
      // A fault lands in idle WITH the fault standing: the state is recoverable by design — pressing again or
      // "Try again" clears it. Nothing about the transcript is lost.
      return {
        ...c,
        state: 'idle',
        partial: '',
        pendingQuery: null,
        bargeInAvailable: false,
        silenceMs: 0,
        fault: { kind: e.kind, message: e.message },
      };
    case 'fault.clear':
      return { ...c, fault: null };
    case 'end':
      return { ...c, state: 'idle', partial: '', pendingQuery: null, bargeInAvailable: false, silenceMs: 0 };
    default:
      return c;
  }
}

/** Human-readable state for the HUD pill (design language: micro label). */
export const STATE_LABEL: Record<VoiceState, string> = {
  idle: 'Ready',
  listening: 'Listening',
  transcribing: 'Transcribing',
  thinking: 'Thinking',
  speaking: 'Speaking',
};
export const STATE_TONE: Record<VoiceState, string> = {
  idle: 'var(--muted)',
  listening: 'var(--pos)',
  transcribing: 'var(--amb-2)',
  thinking: 'var(--amb-2)',
  speaking: 'var(--sky)',
};

/** The fault chip's copy: what stopped the turn, in the trader's words, and that trying again is the way back. */
export const FAULT_LABEL: Record<VoiceFaultKind, string> = {
  permission_denied: 'Microphone permission denied',
  audio_unavailable: 'No audio input available',
  provider_timeout: 'The voice provider did not answer',
  transcription_failed: 'The utterance could not be transcribed',
};
